import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';
import { RefreshCw, ChevronRight } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function CountBadge({ label, value, tone }) {
  const tones = {
    ok: 'text-emerald-400 border-emerald-500/30',
    warn: 'text-amber-400 border-amber-500/30',
    bad: 'text-red-400 border-red-500/30',
    neutral: 'text-zinc-400 border-white/10',
  };
  if (!value) return null;
  return (
    <span className={`text-[10px] font-mono px-2 py-0.5 border rounded-sm ${tones[tone] || tones.neutral}`}>
      {label}:{value}
    </span>
  );
}

export default function AdminSignals() {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [execs, setExecs] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/signals');
      setSignals(data.items || []);
    } catch (e) {
      console.error('[admin/signals] load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (sid) => {
    if (expanded === sid) {
      setExpanded(null);
      return;
    }
    setExpanded(sid);
    if (!execs[sid]) {
      try {
        const { data } = await api.get(`/admin/signals/${sid}/executions`);
        setExecs(prev => ({ ...prev, [sid]: data.items || [] }));
      } catch (e) {
        console.error('[admin/signals] exec load failed', e);
      }
    }
  };

  return (
    <AdminLayout title="Signals" description="All ingested + test signals. Expand a signal to see per-user execution results.">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-zinc-400 font-mono">{loading ? 'Loading…' : `${signals.length} signals`}</p>
        <button onClick={load} data-testid="admin-signals-refresh" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm transition-colors">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="space-y-3" data-testid="admin-signals-list">
        {signals.map(s => {
          const c = s.execution_counts || {};
          const isOpen = expanded === s.signal_id;
          const sourceLabel = s.source === 'bot_ingest' ? 'BOT' : s.source === 'user_test' ? 'TEST' : (s.source || '—').toUpperCase();
          return (
            <div key={s.signal_id} className="border border-white/10 rounded-md bg-[#0c0c0e] overflow-hidden" data-testid={`admin-signal-${s.signal_id}`}>
              <button onClick={() => toggle(s.signal_id)} className="w-full px-5 py-4 flex flex-col lg:flex-row lg:items-center gap-3 hover:bg-white/[0.02] transition-colors text-left">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <ChevronRight size={16} className={`text-zinc-500 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  <span className="font-display text-xl font-bold text-white">{s.symbol}</span>
                  <span className={`text-[10px] font-mono px-2 py-0.5 border rounded-sm ${
                    s.event_type === 'exit' ? 'text-rose-300 border-rose-500/40 bg-rose-500/5' : 'text-sky-300 border-sky-500/40 bg-sky-500/5'
                  }`} title={s.event_type === 'exit' ? `Exit reason: ${s.exit_reason || 'unknown'}` : 'Entry signal'}>
                    {s.event_type === 'exit' ? `EXIT${s.close_pct ? ` ${Math.round(s.close_pct * 100)}%` : ''}` : 'ENTRY'}
                  </span>
                  <span className={`text-xs font-mono px-2 py-0.5 border rounded-sm ${s.side === 'buy' ? 'text-emerald-400 border-emerald-500/30' : 'text-red-400 border-red-500/30'}`}>
                    {s.side?.toUpperCase()}
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500 px-2 py-0.5 border border-white/10 rounded-sm">{sourceLabel}</span>
                  {s.grade && <span className="text-[10px] font-mono gold-text px-2 py-0.5 border gold-border rounded-sm">GRADE {s.grade}</span>}
                  <span className="text-xs text-zinc-500 truncate hidden md:inline">{s.strategy}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <CountBadge label="total" value={c.total} tone="neutral" />
                  <CountBadge label="filled" value={c.filled} tone="ok" />
                  <CountBadge label="partial" value={c.partially_filled} tone="ok" />
                  <CountBadge label="submitted" value={c.submitted} tone="warn" />
                  <CountBadge label="skipped" value={c.skipped} tone="neutral" />
                  <CountBadge label="failed" value={c.failed + (c.rejected || 0)} tone="bad" />
                  <CountBadge label="expired" value={c.expired} tone="warn" />
                </div>
                <div className="text-xs font-mono text-zinc-500 whitespace-nowrap">{fmtDate(s.created_at)}</div>
              </button>
              {isOpen && (
                <div className="border-t border-white/5 px-5 py-4 bg-black/30">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-xs">
                    {s.event_type === 'exit' ? (
                      <>
                        <div><span className="text-zinc-500 font-mono">CLOSE %</span><div className="text-zinc-200 mt-1">{s.close_pct != null ? `${Math.round(s.close_pct * 100)}%` : '—'}</div></div>
                        <div><span className="text-zinc-500 font-mono">EXIT REASON</span><div className="text-zinc-200 mt-1 font-mono">{s.exit_reason || '—'}</div></div>
                      </>
                    ) : (
                      <div><span className="text-zinc-500 font-mono">ALLOC %</span><div className="text-zinc-200 mt-1">{((s.allocation_pct || 0) * 100).toFixed(2)}%</div></div>
                    )}
                    <div><span className="text-zinc-500 font-mono">MODE</span><div className="text-zinc-200 mt-1">{(s.mode || 'paper').toUpperCase()}</div></div>
                    <div><span className="text-zinc-500 font-mono">STATUS</span><div className="text-zinc-200 mt-1">{s.status || '—'}</div></div>
                    <div><span className="text-zinc-500 font-mono">SIGNAL ID</span><div className="text-zinc-400 mt-1 font-mono truncate">{s.signal_id}</div></div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="border-b border-white/10 text-zinc-500 font-mono">
                        <tr>
                          <th className="text-left py-2 pr-3">USER</th>
                          <th className="text-left py-2 pr-3">STATUS</th>
                          <th className="text-left py-2 pr-3">REASON</th>
                          <th className="text-left py-2 pr-3">QTY</th>
                          <th className="text-left py-2 pr-3">LIMIT</th>
                          <th className="text-left py-2 pr-3">NOTIONAL</th>
                          <th className="text-left py-2 pr-3">ATTEMPTS</th>
                          <th className="text-left py-2">ORDER ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(execs[s.signal_id] || []).map(e => (
                          <tr key={e.execution_id} className="border-t border-white/5">
                            <td className="py-2 pr-3 text-zinc-200">{e.user_email}</td>
                            <td className="py-2 pr-3 font-mono">{e.status}</td>
                            <td className="py-2 pr-3 text-zinc-400">{e.skip_reason || e.error_message || '—'}</td>
                            <td className="py-2 pr-3">{e.qty ?? '—'}</td>
                            <td className="py-2 pr-3">{e.limit_price ? `$${e.limit_price}` : '—'}</td>
                            <td className="py-2 pr-3">{e.target_notional ? `$${(+e.target_notional).toFixed(2)}` : '—'}</td>
                            <td className="py-2 pr-3">{e.attempts ?? '—'}</td>
                            <td className="py-2 text-zinc-500 font-mono">{e.alpaca_order_id ? e.alpaca_order_id.slice(0, 12) + '…' : '—'}</td>
                          </tr>
                        ))}
                        {(execs[s.signal_id] || []).length === 0 && (
                          <tr><td colSpan={8} className="py-6 text-center text-zinc-500">No execution records for this signal.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {!loading && signals.length === 0 && (
          <div className="text-center text-sm text-zinc-500 py-10 border border-white/10 rounded-md">No signals yet.</div>
        )}
      </div>
    </AdminLayout>
  );
}
